import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { from, Observable } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";

import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { PaymentsService } from "../payments.service";

/** Every Score-area route starts with this - the landing page, the session page and the Buffer views. */
export const SCORE_AREA_ROUTE = "/score";

/** Whether `url` (ignoring query and fragment) is inside the Score area. */
export function isScoreAreaUrl(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return path === SCORE_AREA_ROUTE || path.startsWith(`${SCORE_AREA_ROUTE}/`);
}

/**
 * Shared by the Score contextual lessons (FLIP-366, release switch `score_contextual`): S01B, RS01,
 * RS02, FO01, SS01, SS02.
 *
 * - `enabled$()` is the boot gate every lesson uses: a confirmed subscription (that's when
 *   app.component's authenticated fan-out starts the flag fetch), then the `onboarding-walkthrough`
 *   flag. No area-level flag exists yet, so `score_contextual` is the step path, not a flag.
 * - The entry sequence the suppression rules refer to ("never in the same entry as RS01..."): one
 *   continuous visit to the Score area (/score, the scoring session, the Buffer views), ended by
 *   navigating anywhere else. Lessons record themselves here when presented; later lessons in the
 *   same entry check it and hold back.
 */
@Injectable({ providedIn: "root" })
export class ScoreContextualEntryService {
  private readonly presented = new Set<string>();

  constructor(
    router: Router,
    private featureFlagService: FeatureFlagService,
    private paymentsService: PaymentsService
  ) {
    router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (!isScoreAreaUrl(event.urlAfterRedirects ?? event.url)) {
          this.presented.clear();
        }
      });
    // Logout emits false - the next account starts its own entry.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => this.presented.clear());
  }

  /** Emits once, true, when the lessons may run - never if the flag is off. */
  enabled$(): Observable<true> {
    return this.paymentsService.subscriptionActive$.pipe(
      filter((active) => active),
      take(1),
      switchMap(() => from(this.featureFlagService.init())),
      filter(() => this.featureFlagService.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)),
      map(() => true as const)
    );
  }

  /** Call when a lesson is presented. */
  markPresented(lessonId: string): void {
    this.presented.add(lessonId);
  }

  /** Whether any of `lessonIds` was presented in the current entry. */
  presentedThisEntry(...lessonIds: string[]): boolean {
    return lessonIds.some((id) => this.presented.has(id));
  }
}
